import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { FormWrapper } from '@/form-control'
import { NumberField, TextAreaField, TextField, ToggleField } from '@/form-control/fields'
import ROUTES from '@/ROUTES'

import { IRoadmapMilestone } from '@/MODELS/roadmapMilestone.model'

import { api } from '@/services/api.service'
import { useResource } from '@/services/resource.service'

import AdminPageHeader from '@/components/AdminPageHeader'
import Button from '@/components/Button'

export default function AdminRoadmapMilestoneForm(props: { id?: string }) {
    const params = useParams()
    const id = props?.id ?? params?.id ?? 'new'
    const isNew = id === 'new'
    const [usageWarning, setUsageWarning] = useState<string | null>(null)
    const [allMilestones] = useResource<IRoadmapMilestone[]>(ROUTES.admin.roadmapMilestone)

    // Check usage when editing an existing milestone
    const checkUsage = async (milestoneId: string) => {
        if (milestoneId === 'new') return
        try {
            const res = await api<{ usedInActiveSequences: number; sequences: any[] }>(`${ROUTES.admin.roadmapMilestone}/${milestoneId}/usage`)
            if (res?.data?.usedInActiveSequences && res.data.usedInActiveSequences > 0) {
                const count = res.data.usedInActiveSequences
                setUsageWarning(
                    `This milestone is used in ${count} active sequence${count > 1 ? 's' : ''}. Deactivating it may affect roadmap generation for those age/diagnosis combinations.`
                )
            } else {
                setUsageWarning(null)
            }
        } catch {
            // Silently fail — usage check is informational
        }
    }

    useEffect(() => {
        checkUsage(id)
    }, [id])

    return (
        <>
            <AdminPageHeader heading={isNew ? 'Create New Milestone' : 'Edit Milestone'} />
            <FormWrapper<IRoadmapMilestone>
                endpoint={ROUTES.admin.roadmapMilestone}
                id={id}
                redirectAfterSubmit='/admin/roadmap-milestones'
                defaultValues={{ isActive: true, order: 0 }}
                additionalSubmissionRowContent={
                    <div className='flex items-center justify-between w-full gap-x-2 h-fit'>
                        <Button.Back />
                    </div>
                }
            >
                {(f, { formValues }) => {
                    const existingMilestone = useMemo(() => {
                        if (!isNew || !allMilestones || !formValues.key) return null
                        return allMilestones.find(
                            (m) => m.key === (formValues.key as string)
                        ) || null
                    }, [allMilestones, formValues.key, isNew])

                    return (
                        <div className='border rounded-xl mt-8'>
                            <section className='bg-white rounded-xl shadow-sm border-gray-200 overflow-hidden'>
                                <div className='px-6 py-3 space-y-3'>
                                    <div className='grid lg:grid-cols-2 gap-6'>
                                        {isNew ? (
                                            <TextField {...f('key')} label='Key (unique identifier)' />
                                        ) : (
                                            <div>
                                                <label className='block text-sm font-medium text-gray-700 mb-1'>Key</label>
                                                <p className='text-sm text-gray-900 font-mono py-1'>{formValues.key as string}</p>
                                                <p className='text-xs text-gray-400 mt-1'>
                                                    The key cannot be changed because sequences and existing roadmaps reference it.
                                                </p>
                                            </div>
                                        )}
                                        <TextField {...f('title')} label='Display Title' />
                                        <NumberField {...f('order')} label='Sort Order' />
                                        <div>
                                            <ToggleField {...f('isActive')} label='Active' />
                                            <p className='text-xs text-gray-500 mt-1'>
                                                Inactive milestones are excluded from new roadmap generation but remain visible on existing user roadmaps.
                                            </p>
                                        </div>
                                        <div className='lg:col-span-2'>
                                            <TextAreaField {...f('description')} label='Description (supports ${childName} template)' rows={3} />
                                        </div>
                                        <div className='lg:col-span-2'>
                                            <TextAreaField {...f('focus')} label='Focus Areas' rows={2} />
                                        </div>
                                    </div>

                                    {existingMilestone && (
                                        <div className='p-3 bg-amber-50 border border-amber-200 rounded-lg'>
                                            <p className='text-sm text-amber-800'>
                                                A milestone with the key "{formValues.key}" already exists.{' '}
                                                <Link
                                                    to={`/admin/roadmap-milestone/${existingMilestone._id}`}
                                                    className='font-medium underline hover:text-amber-900'
                                                >
                                                    Edit the existing milestone
                                                </Link>{' '}
                                                instead of creating a new one.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </section>
                            {usageWarning && (
                                <div className='mx-6 my-4 p-3 bg-amber-50 border border-amber-200 rounded-lg'>
                                    <p className='text-sm text-amber-700'>{usageWarning}</p>
                                </div>
                            )}
                        </div>
                    )
                }}
            </FormWrapper>
        </>
    )
}
